import debounceAsync from '~/utils/debounceAsync';
import { useCartStore } from '~/modules/cart/stores/cartStore';
import { useCheckoutStore } from '~/modules/checkout/stores/checkoutStore';
import { useAuthStore } from '~/modules/auth/stores/authStore';
import { useToastStore } from '~/stores/toastStore';
import { tabBarNavigateStore } from '~/modules/tabBar/stores/tabBarNavigateStore';
import type { ICartAddParams, IProductService, TCartErrors } from '~/modules/cart/types/cart-types';
import extractResponseErrors from '~/modules/auth/utils/extractResponseErrors';
import { getAdditionalProductBySlug } from '~/modules/cart/utils/cartHelpers';
import { useMindbox } from '~/uses/useMindbox';
import { useDataLayer } from '~/uses/useDataLayer';
import { mapCartLineToDL } from '~/utils/dataLayerMappers';

const DEBOUNCE_DELAY = 200;

let bonusAbortController: AbortController | null = null;
let bonusChangingCount = 0;

export const useCart = () => {
  const { $api } = useNuxtApp() as NuxtAppType;

  const authStore = useAuthStore();
  const cartStore = useCartStore();
  const checkoutStore = useCheckoutStore();
  const toastStore = useToastStore();
  const tabBarStore = tabBarNavigateStore();
  const isTablet = useState<boolean>('isTablet');

  const errors = reactive<TCartErrors>({});

  const { setCart, clearCart } = useMindbox();
  const { removeFromCart: dlRemoveFromCart, addToCart: dlAddToCart } = useDataLayer();

  const syncMindboxCart = () => {
    const items = cartStore.activeCartLines.map((line) => ({
      productId: line.product_external_key ?? line.product_id,
      price: Number(line.final_price.amount),
      quantity: line.quantity.amount,
    }));

    if (items.length === 0) {
      clearCart();
    } else {
      setCart(items);
    }
  };

  const errorMessage = ref('');

  const promocodeError = computed(() => errors['code']?.[0]);

  const {
    total,
    subtotal,
    discountPercent,
    discountPrice,
    quantity,
    promocode,
    isPromocodeApplied,
    availableBonusCount,
    accruedBonusCount,
    spentBonusCount,
  } = storeToRefs(cartStore);

  const { bonusAmount } = storeToRefs(checkoutStore);

  const handleError = (e: any) => {
    Object.assign(errors, extractResponseErrors(e));

    if (e.response && e.response._data) {
      const { message } = e.response._data;
      if (message) {
        errorMessage.value = message;
      }
    } else {
      errorMessage.value = 'Сетевая ошибка';
      console.error('Сетевая ошибка:', e);
    }
  };

  const addToCart = async (params: ICartAddParams) => {
    try {
      const response = await $api.cart.addToCart(params);
      if (!response.success) {
        throw new Error(response.message);
      }

      // getCount();
      if (!params.relatedLineId) {
        quantity.value += params.quantity;
      }

      getCartData().then((cartData) => {
        if (!cartData) return;
        const items = cartData.lines.map((line) => ({
          productId: line.product_external_key ?? line.product_id,
          price: Number(line.final_price.amount),
          quantity: line.quantity.amount,
        }));
        setCart(items);
      }).catch(() => {});

      return response;
    } catch (e: any) {
      toastStore.showToast(e.response._data.message);
      console.error(e);
      throw e;
    }
  };

  const getCartData = async () => {
    const response = await $api.cart.getCart();
    if (!response.success) return;

    return response.data;
  };

  const getCartCheckoutData = async () => {
    const response = await $api.orders.initOrder({
      lines: cartStore.activeOrderLines,
      bonus_amount: bonusAmount.value,
    });
    if (!response.success) return;

    return response.data;
  };

  const getCartCount = async () => {
    const response = await $api.cart.getCount();
    if (!response.success) return 0;

    return response.data.amount;
  };

  const setupCartCount = async () => {
    quantity.value = await getCartCount();
  };

  const resetCartCounters = () => {
    total.value = 0;
    subtotal.value = 0;
    discountPrice.value = 0;
    availableBonusCount.value = 0;
    accruedBonusCount.value = 0;
    spentBonusCount.value = 0;

    if (bonusChangingCount === 0) {
      bonusAmount.value = 0;
    }
  };

  const recalculate = async () => {
    // Ноль активных линий (например, в корзине только неликвид) —
    // легитимное состояние, но запрос orders/init с lines: [] бессмысленен.
    // Обнуляем счётчики локально и не дёргаем бэк.
    if (!cartStore.activeOrderLines.length) {
      resetCartCounters();
      return;
    }

    // Нам необходимо запрашивать данные из чекаута,
    // т.к. на данный момент не реализованы чекбоксы в корзине на беке
    const data = await getCartCheckoutData();

    if (!data) return;

    cartStore.updateCartCounters(data);

    // Бэк может срезать введённую сумму бонусов до допустимой —
    // синхронизируем локальное значение с фактически списанным,
    // но только если onBonusChange сейчас не выполняется
    if (bonusChangingCount === 0 && (data.spent_bonus_count ?? 0) !== bonusAmount.value) {
      bonusAmount.value = data.spent_bonus_count ?? 0;
    }
  };

  const onBonusChange = async (value: number) => {
    const previousBonusAmount = bonusAmount.value;
    bonusAmount.value = value;

    if (!cartStore.activeOrderLines.length) return;

    bonusAbortController?.abort();
    bonusAbortController = new AbortController();

    bonusChangingCount++;
    try {
      const response = await $api.orders.initOrder(
        { lines: cartStore.activeOrderLines, bonus_amount: value },
        bonusAbortController.signal,
      );
      if (!response.success) {
        bonusAmount.value = previousBonusAmount;
        return;
      }

      cartStore.updateCartCounters(response.data);

      if ((response.data.spent_bonus_count ?? 0) !== value) {
        bonusAmount.value = response.data.spent_bonus_count ?? 0;
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        bonusAmount.value = previousBonusAmount;
        throw e;
      }
    } finally {
      bonusChangingCount--;
    }
  };

  const removeLine = async (lineId: string | number) => {
    const response = await $api.cart.remove({ lineId });
    if (!response.success) return;

    const index = cartStore.getItemIndex(lineId);
    if (index === -1) return;

    const line = cartStore.cartItems[index].line;
    dlRemoveFromCart(mapCartLineToDL(line));

    if (line.available) {
      cartStore.cartItems[index].removed = true;
    } else {
      cartStore.cartItems.splice(index, 1);
    }

    await recalculate();
    syncMindboxCart();
  };

  const restoreLine = async (lineId: string | number) => {
    const index = cartStore.getItemIndex(lineId);
    if (index === -1) return;

    const line = cartStore.cartItems[index].line;

    // Преобразуем selected_additional в формат services для API
    const services: IProductService[] = [];

    if (line.selected_additional?.length && line.additional_products?.length) {
      for (const service of line.selected_additional) {
        // Находим product_id по slug в additional_products
        const additionalProduct = getAdditionalProductBySlug(line, service.slug);

        if (additionalProduct) {
          services.push({
            product_id: additionalProduct.id,
            offer_id: service.offer_id,
            metadata: service.metadata,
          });
        }
      }
    }

    // Получаем список существующих line_id до добавления
    const existingLineIds = new Set(cartStore.cartItems.filter((item) => !item.removed).map((item) => item.line.id) || []);

    await addToCart({
      productId: line.product_id,
      quantity: line.quantity.amount || 1,
      offerId: line.offer_id,
      services: services.length > 0 ? services : undefined,
    });

    cartStore.cartItems[index].removed = false;

    const data = await getCartData();
    if (!data) return;

    // Находим новую строку - ту, которой не было в existingLineIds
    const newLine = data.lines.find((item) => !existingLineIds.has(item.id));
    if (!newLine) return;

    cartStore.updateCartItem(line.id, newLine);

    await recalculate();
  };

  const setQuantity = async (lineId: string | number, amount: number) => {
    const indexBefore = cartStore.getItemIndex(lineId);
    const lineBefore = indexBefore !== -1 ? cartStore.cartItems[indexBefore].line : null;
    const oldAmount = lineBefore?.quantity.amount ?? 0;

    const response = await $api.cart.setQuantity({ lineId, quantity: amount });
    if (!response.success) return;

    const index = cartStore.getItemIndex(lineId);
    if (index === -1) return;

    if (lineBefore) {
      const delta = amount - oldAmount;
      if (delta > 0) {
        dlAddToCart(mapCartLineToDL(lineBefore, delta));
      } else if (delta < 0) {
        dlRemoveFromCart(mapCartLineToDL(lineBefore, Math.abs(delta)));
      }
    }

    cartStore.cartItems[index].line.quantity.amount = amount;

    await recalculate();
    syncMindboxCart();
  };

  const toggleLineChecked = async (lineId: string | number, value: boolean) => {
    await recalculate();
  };

  const clearAll = async () => {
    const response = await $api.cart.clear();
    if (!response.success) return;

    cartStore.cartItems.length = 0;
    quantity.value = 0;
    clearCart();
  };

  const clearNotAvailable = async () => {
    const response = await $api.cart.clearNotAvailable();
    if (!response.success) return;

    cartStore.initCartItems(cartStore.availableItems.map((i) => i.line));
    syncMindboxCart();
  };

  const setPromocode = async (code: string) => {
    try {
      await $api.cart.setPromocode(code);

      if (code.length) {
        isPromocodeApplied.value = true;
      }
    } catch (e) {
      isPromocodeApplied.value = false;
      throw e;
    }
  };

  const init = async () => {
    try {
      const data = await getCartData();

      if (data) {
        cartStore.initCart(data);

        // Необходимо пересчитывать, т.к. бэк не умеет в чекбоксы
        await recalculate();
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  const fetchCart = async () => {
    try {
      const data = await getCartData();

      if (data) {
        cartStore.initCartItems(data.lines);

        // Необходимо пересчитывать, т.к. бэк не умеет в чекбоксы
        await recalculate();
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  const doCartAction = async (callback: Awaited<any>) => {
    try {
      errorMessage.value = '';
      cartStore.isCartLoading = true;

      await callback;
    } catch (e) {
      handleError(e);
    } finally {
      cartStore.isCartLoading = false;
    }
  };

  const onRemoveLine = async (lineId: string | number) => {
    await doCartAction(removeLine(lineId));
  };

  const onRestoreLine = async (lineId: string | number) => {
    await doCartAction(restoreLine(lineId));
  };

  const onSetQuantity = debounceAsync(async (lineId: string | number, amount: number) => {
    await doCartAction(setQuantity(lineId, amount));
  }, DEBOUNCE_DELAY);

  const onToggleLineChecked = async (lineId: string | number, value: boolean) => {
    await doCartAction(toggleLineChecked(lineId, value));
  };

  const onClearAll = async () => {
    await doCartAction(clearAll());
  };

  const onClearNotAvailable = async () => {
    await doCartAction(clearNotAvailable());
  };

  const doFetch = async () => {
    await doCartAction(fetchCart());
  };

  const onCreateOrder = () => {
    checkoutStore.initCheckoutLines(cartStore.activeCartLines);

    if (authStore.isUserAuthorized) {
      navigateTo('/checkout/');
    } else {
      const segment = tabBarStore.activeSegment;

      authStore.toggleAuth(true, () => {
        if (isTablet.value) {
          tabBarStore.setActiveSegment(segment);
        }

        navigateTo('/checkout/');
      });
    }
  };

  const bulkItems = async (orderLines: any) => {
    const response = await $api.cart.bulk(orderLines);
    if (!response.success) return;
  };

  const onSetPromocode = async (code: string) => {
    await doCartAction(setPromocode(code));

    await recalculate();

    promocode.value = code || '';
  };

  const onClearPromocode = async () => {
    promocode.value = '';
    isPromocodeApplied.value = false;

    await doCartAction(setPromocode(''));

    await recalculate();

  };

  const onFieldUpdate = (name: string) => {
    errors[name] = [];

    errorMessage.value = '';
  };

  return {
    quantity,
    total,
    subtotal,
    discountPercent,
    discountPrice,
    promocode,
    isPromocodeApplied,
    availableBonusCount,
    accruedBonusCount,
    spentBonusCount,
    bonusAmount,
    onBonusChange,
    errorMessage,
    promocodeError,
    setupCartCount,
    getCartData,
    addToCart,
    onCreateOrder,
    onSetQuantity,
    onRestoreLine,
    onRemoveLine,
    onToggleLineChecked,
    onClearAll,
    onClearNotAvailable,
    onSetPromocode,
    onClearPromocode,
    removeLine,
    init,
    doFetch,
    recalculate,
    bulkItems,
    onFieldUpdate,
    setPromocode,
  };
};
